import { Button, Stack } from '@mui/material';
import {
  LandingPage,
  type LandingPagePartner,
  type LandingPagePlansData,
} from '@provision/landing-page-ui';
import type { LandingPageConfig } from '@provision/landing-page-utils';
import { useEffect } from 'react';
import { DynamicIcon } from 'src/components';
import { PreviewViewportToggle } from 'src/pages/Settings/memberships/components/preview-viewport-toggle';
import {
  LandingPagePreviewContainer,
  PreviewViewportFrame,
} from 'src/pages/Settings/memberships/styled';
import { buildLandingPagePreviewUrl } from 'src/pages/Settings/memberships/utils/build-landing-page-preview-url';
import {
  clearLandingPagePreview,
  writeLandingPagePreview,
} from 'src/pages/Settings/memberships/utils/landing-page-preview-channel';
import type { PreviewViewport } from 'src/store/memberships-config';
import { useNavigateRoute } from 'src/utils/routing';

type LandingPagePreviewProps = {
  config: LandingPageConfig;
  plans: LandingPagePlansData;
  viewport: PreviewViewport;
  onViewportChange: (viewport: PreviewViewport) => void;
  partner?: LandingPagePartner;
  logoUrl?: string;
  brandId?: string;
};

export const LandingPagePreview = ({
  config,
  plans,
  viewport,
  onViewportChange,
  partner,
  logoUrl,
  brandId,
}: LandingPagePreviewProps) => {
  const { partnerPrefix } = useNavigateRoute();
  const previewUrl = buildLandingPagePreviewUrl(partnerPrefix, brandId);

  // Cleared on unmount, which also covers logout and leaving the page, since both unmount this.
  // A brand switch re-runs the effect, so the previous brand's entry goes with it.
  useEffect(() => {
    if (!brandId) return;

    writeLandingPagePreview(brandId, { config, plans, partner, logoUrl });

    return () => {
      clearLandingPagePreview(brandId);
    };
  }, [brandId, config, plans, partner, logoUrl]);

  return (
    <LandingPagePreviewContainer>
      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ width: '100%' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DynamicIcon name="open_in_new" sx={{ fontSize: '1rem' }} />}
          onClick={() => window.open(previewUrl, '_blank')}
        >
          Open in new tab
        </Button>
        <PreviewViewportToggle value={viewport} onChange={onViewportChange} />
      </Stack>
      <PreviewViewportFrame
        isMobile={viewport === 'mobile'}
        data-testid="preview-viewport-frame"
      >
        <LandingPage config={config} plans={plans} partner={partner} logoUrl={logoUrl} />
      </PreviewViewportFrame>
    </LandingPagePreviewContainer>
  );
};
